<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { computed, onBeforeUnmount, ref, watch, type CSSProperties } from 'vue'

import type { DrawerProps } from './types'
import { DRAWER_WIDTH_BY_SIZE, resolveDrawerWidth } from './width'

import { injectUiLabels } from '@/shared/i18n'
import { FloatingLayer, useOverlayFocusReturn } from '@/shared/ui/overlay'

const open = defineModel<boolean>({ default: false })

/**
 * Attributes reach the dialog root explicitly. This component's own root is `FloatingLayer`, which
 * declares `inheritAttrs: false` and, in its `as="template"` branch, renders nothing but the slot —
 * so a `class` or `data-testid` written on this component was silently DROPPED before ever reaching
 * the DOM. Harmless while every host kept its styling in a scoped style block (scoped rules
 * reach slot content through the data-v attribute, not through this root class), it stopped being
 * harmless once host CSS moved onto the component tag as Tailwind utilities: those utilities were
 * dead, and a `data-testid` on the tag was unfindable.
 */
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<DrawerProps>(), {
  placement: 'right',
  size: 'lg',
  offset: 0,
  minWidth: '40rem',
  maxWidth: 'calc(100vw - 1rem)',
  resizable: false,
  closeOnOutside: true,
  hideClose: false,
})

// `resize` lets a host that placed a second surface beside this one (see `offset`) follow the
// user's drag: old's `o-sidebar-modal` pair did the same through `@width-change`.
const emit = defineEmits<{ close: []; resize: [width: number] }>()
const uiLabels = injectUiLabels()
const resolvedCloseLabel = computed(() => props.closeLabel ?? uiLabels.close)
const resolvedResizeLabel = computed(() => props.resizeLabel ?? uiLabels.resize)
const resizedWidth = ref<number | null>(null)
const { setFocusScope, completeLeave } = useOverlayFocusReturn(open)
const panelStyle = computed(() => ({ width: `${panelWidth()}px` }))
/** Applied to the VIEWPORT, not the panel: padding moves the panel without resizing it, and leaves
 *  the backdrop covering the whole screen. */
const viewportStyle = computed(() => ({
  paddingInlineEnd: props.placement === 'right' ? offsetCss() : undefined,
  paddingInlineStart: props.placement === 'left' ? offsetCss() : undefined,
}))
let blockNextOutsideClose = false
let startX = 0
let startWidth = 0

function offsetCss(): string | undefined {
  const value = props.offset
  if (value === 0 || value === '0') return undefined
  return typeof value === 'number' ? `${value}px` : value
}

function viewportWidth(): number {
  return window.innerWidth || document.documentElement.clientWidth
}

function initialWidth(): number {
  const fallback = props.resizable ? '50vw' : DRAWER_WIDTH_BY_SIZE[props.size]
  return resolveDrawerWidth(props.width ?? fallback, viewportWidth())
}

function panelWidth(): number {
  const override = props.widthOverride
  const width =
    override !== undefined && override !== ''
      ? resolveDrawerWidth(override, viewportWidth())
      : (resizedWidth.value ?? initialWidth())
  if (props.resizable) return clampWidth(width)
  return Math.min(resolveDrawerWidth(props.maxWidth, viewportWidth()), width)
}

function clampWidth(value: number): number {
  const viewport = viewportWidth()
  const minimum = resolveDrawerWidth(props.minWidth, viewport)
  const maximum = resolveDrawerWidth(props.maxWidth, viewport)
  return Math.min(maximum, Math.max(minimum, value))
}

function close(): void {
  open.value = false
  emit('close')
}

function onBackdropPointerDown(): void {
  if (props.closeOnOutside) return
  blockNextOutsideClose = true
  setTimeout(() => {
    blockNextOutsideClose = false
  })
}

// A confirm dialog raised from inside this drawer is a sibling top-level mount, not this
// Dialog's Vue-tree descendant (see Modal.vue's `onDialogClose` for the full explanation) —
// only the topmost blocking overlay may act on its own reflexive close.
function onDialogClose(isTopmostBlocking: boolean): void {
  if (blockNextOutsideClose || !isTopmostBlocking) {
    blockNextOutsideClose = false
    return
  }
  close()
}

function onPointerMove(event: PointerEvent): void {
  const delta = props.placement === 'right' ? startX - event.clientX : event.clientX - startX
  resizedWidth.value = clampWidth(startWidth + delta)
  emit('resize', resizedWidth.value)
}

function stopResize(): void {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', stopResize)
}

function startResize(event: PointerEvent): void {
  const handle = event.currentTarget as HTMLElement | null
  const panel = handle?.closest('.ui-drawer__panel') as HTMLElement | null
  if (!props.resizable || !panel) return
  event.preventDefault()
  startX = event.clientX
  startWidth = panel.offsetWidth
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', stopResize, { once: true })
}

watch(
  open,
  (isOpen) => {
    if (!isOpen) {
      resizedWidth.value = null
      stopResize()
    }
  },
  { flush: 'sync' },
)
onBeforeUnmount(() => {
  stopResize()
})
</script>

<template>
  <FloatingLayer
    v-slot="{ style: layerStyle, layerAttributes, isTopmostBlocking }"
    kind="drawer"
    :active="open"
    as="template"
  >
    <TransitionRoot :show="open" as="template" @after-leave="completeLeave">
      <Dialog
        v-bind="{ ...$attrs, ...layerAttributes }"
        class="ui-drawer relative"
        :style="[$attrs.style as CSSProperties, layerStyle]"
        @close="onDialogClose(isTopmostBlocking)"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity duration-[var(--ui-overlay-enter-duration)] ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity duration-[var(--ui-overlay-leave-duration)] ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="ui-drawer__backdrop fixed inset-0 bg-[color:var(--ui-overlay-backdrop)]"
            @pointerdown="onBackdropPointerDown"
          />
        </TransitionChild>

        <div
          :ref="setFocusScope"
          class="ui-drawer__viewport pointer-events-none fixed inset-0 flex"
          :class="[
            `ui-drawer__viewport--${placement}`,
            placement === 'right' ? 'justify-end' : 'justify-start',
          ]"
          :style="viewportStyle"
        >
          <TransitionChild
            as="template"
            enter="transition-transform duration-[var(--ui-drawer-slide-duration)] ease-in-out sm:duration-[var(--ui-drawer-slide-duration-wide)]"
            :enter-from="placement === 'right' ? 'translate-x-full' : '-translate-x-full'"
            enter-to="translate-x-0"
            leave="transition-transform duration-[var(--ui-drawer-slide-duration)] ease-in-out sm:duration-[var(--ui-drawer-slide-duration-wide)]"
            leave-from="translate-x-0"
            :leave-to="placement === 'right' ? 'translate-x-full' : '-translate-x-full'"
          >
            <DialogPanel
              class="ui-drawer__panel pointer-events-auto relative flex h-full max-w-[calc(100vw_-_var(--ui-drawer-viewport-gutter))] flex-col overflow-hidden bg-surface font-sans text-fg shadow-[var(--ui-overlay-panel-shadow)]"
              :style="panelStyle"
            >
              <button
                v-if="resizable"
                type="button"
                class="ui-drawer__resize absolute inset-y-0 z-raised w-xs cursor-ew-resize border-0 bg-transparent p-0"
                :class="[
                  `ui-drawer__resize--${placement}`,
                  placement === 'right' ? 'left-0' : 'right-0',
                ]"
                :aria-label="resolvedResizeLabel"
                @pointerdown="startResize"
              />

              <header
                class="ui-drawer__header flex items-center justify-between gap-sm border-b border-border px-lg pt-xl pb-md"
              >
                <DialogTitle
                  v-if="title || $slots.header"
                  as="h2"
                  class="ui-drawer__title m-0 text-xl font-semibold"
                >
                  <slot name="header">{{ title }}</slot>
                </DialogTitle>
                <button
                  v-if="!hideClose"
                  type="button"
                  class="ui-drawer__close grid size-8 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-2xl text-muted"
                  @click="close"
                >
                  <span class="ui-drawer__sr-only sr-only">{{ resolvedCloseLabel }}</span>
                  <span aria-hidden="true">×</span>
                </button>
              </header>

              <div class="ui-drawer__body min-h-0 flex-1 overflow-auto px-lg py-md"><slot /></div>
              <footer
                v-if="$slots.footer"
                class="ui-drawer__footer flex items-center justify-end gap-sm border-t border-border px-lg py-md"
              >
                <slot name="footer" />
              </footer>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </FloatingLayer>
</template>
